// react
import { useCallback, useEffect, useState } from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';

// mui
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import Stack from '@mui/material/Stack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// notistack
import { SnackbarProvider } from 'notistack';

// routing
import Routes from './routes';

// sentry
import * as Sentry from '@sentry/react';

// styling
import themes from './themes';
import './App.css';

// project utils
import useAppDataSource from 'hooks/useAppDataSource';
import { setAppConfigData } from 'store/features/appConfigSlice';
import { setAppDataSourceId, setAppDataSources } from 'store/features/appDataSourceSlice';
import handleError from 'utils/handle-error';
import callAzureFunctionPublic from 'utils-ts/call-azure-function-public';
import defaultAppConfig from 'constants/appConfig';
import { getImageUrl } from 'utils/entities';

// component
import NavigationScroll from 'layout/NavigationScroll';
import { notistackClasses, notistackCustomSnackbars } from 'views/utilities/NotistackClasses';
import BrandLogoLoader from 'ui-component/BrandLogoLoader';
import AlertDialog from 'ui-component/dialogs/AlertDialog';
import AppLogout from './AppLogout';
import { AuthProvider } from './AuthProvider';
import UnsupportedBrowserBanner from 'ui-component/banners/UnsupportedBrowserBanner';
// eslint-disable-next-line import/no-unresolved
import { isSupportedBrowser } from 'vite-plugin-supported-browserslist/web';

import { appConfigPaths } from '@a1/shared';

// ==============================|| APP ||============================== //
const App = () => {
  const customization = useSelector((state) => state.customization);
  const { isLoggedIn, data: userData } = useSelector((state) => state.user);
  const appDataSourceId = useSelector((state) => state.appDataSource?.selectedId);
  const dispatch = useDispatch();

  const { useGetAppDataSources } = useAppDataSource();
  const appDataSources = useGetAppDataSources();

  const [loadingConfig, setLoadingConfig] = useState(true);

  // Unsupported browser banner state
  const [showUnsupportedBanner, setShowUnsupportedBanner] = useState(false);

  const loadAppConfigurations = useCallback(async () => {
    try {
      setLoadingConfig(true);

      // Fetch app configurations with localStorage: false excluded
      // TODO trim down this list to only the necessary keys, we should load in ones like confirmation text only when they're needed
      const res = await callAzureFunctionPublic({
        url: 'public/app-configurations',
        method: 'get',
        params: {
          keys: [
            appConfigPaths.generalSettings.path,
            appConfigPaths.entities.path,
            appConfigPaths.respondents.path,
            appConfigPaths.images.path,
            appConfigPaths.bookings.path,
            appConfigPaths.payments.path,
            appConfigPaths.surveys.path,
            // appConfigPaths.bookingConfirmationText.path,
            // appConfigPaths.bookingAffiliateConfirmationText.path,
            // appConfigPaths.cancelledBookingText.path,
            // appConfigPaths.didNotAttendBookingText.path,
            // appConfigPaths.rescheduledBookingText.path,
            // appConfigPaths.reminderBookingText.path,
            appConfigPaths.helpText.path,
            appConfigPaths.others.path,
            appConfigPaths.premium.path,
            appConfigPaths.integrations.path
          ]
        }
      });

      const remoteConfigs = res.data?.configs || {};

      // Merge remote configs with default ones
      const data = {};
      Object.keys(defaultAppConfig).forEach((key) => {
        data[key] = { ...defaultAppConfig[key], ...remoteConfigs[key] };
      });

      // Update config in store
      dispatch(setAppConfigData({ data }));

      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }

      link.href = getImageUrl('favicon', data);
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingConfig(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadAppConfigurations();
  }, [loadAppConfigurations]);

  const loadEnvironments = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const res = await callAzureFunctionPublic({
        url: `/environments`,
        method: 'get'
      });

      const appDataSources = res.data;
      if (appDataSources) {
        dispatch(setAppDataSources(appDataSources));
        if (appDataSourceId && !appDataSources[appDataSourceId]) {
          dispatch(setAppDataSourceId(null));
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingConfig(false);
    }
  }, [dispatch, appDataSourceId]);

  useEffect(() => {
    loadEnvironments();
  }, [loadEnvironments]);

  // Check for URL parameter and set app data source to parameter if found
  useEffect(() => {
    if (!appDataSources) return;
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');
    if (sourceParam in appDataSources) {
      dispatch(setAppDataSourceId(sourceParam));
    }
  }, [appDataSources, dispatch]);

  useEffect(() => {
    if (userData) {
      const { id, emailAddress, firstName, lastName, type } = userData;
      const username = `${firstName || ''} ${lastName || ''}`.trim() || undefined;

      Sentry.setUser({ id, email: emailAddress, username, type });
    } else {
      Sentry.setUser(null); // Clear the user when they log out
    }
  }, [userData]);

  // Determine unsupported browser banner visibility on first render
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem('unsupportedBrowserBannerDismissed') === 'true';
      const supported = isSupportedBrowser();
      if (!supported && !dismissed) {
        setShowUnsupportedBanner(true);
      }
    } catch (_) {
      // In case localStorage or detection fails, do nothing
    }
  }, []);

  return (
    <>
      {loadingConfig ? (
        <Stack justifyContent="center" alignItems="center" sx={{ height: '100vh', backgroundColor: 'grey.200' }}>
          <BrandLogoLoader width={200} />
        </Stack>
      ) : (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={themes(customization)}>
            <CssBaseline />
            <NavigationScroll>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <SnackbarProvider Components={notistackCustomSnackbars} classes={notistackClasses} maxSnack={5}>
                  <AppLogout>
                    <AuthProvider>
                      <Routes isAuthenticated={isLoggedIn} />
                      <AlertDialog />
                      {showUnsupportedBanner && <UnsupportedBrowserBanner setShow={setShowUnsupportedBanner} />}
                    </AuthProvider>
                  </AppLogout>
                </SnackbarProvider>
              </LocalizationProvider>
            </NavigationScroll>
          </ThemeProvider>
        </StyledEngineProvider>
      )}
    </>
  );
};

export default App;
